import anime, { AnimeTimelineInstance } from "animejs"
import React, { FC, useEffect, useRef, useState } from "react"
import styled, { css } from "styled-components"

import { Chat, IChatScene } from "./Chat"
import { getChatImage } from "./animationHelpers"

// Animation: Framing and Surrounding UI
const Device = styled.div`
  /* stylelint-disable-next-line */
  --frame-roundedness: 36px;
  --device-ui-height: 80px;

  position: relative;
  display: flex;
  flex-flow: column;
  width: 100%;
  max-width: 320px;
  height: 100%;
  min-height: 560px;
  aspect-ratio: 10 / 21.5; /* iPhone 14 Pro screen ratio */
  margin: 0 auto;
  overflow: hidden;
  user-select: none;
  background: white;
  border-radius: var(--frame-roundedness);
  box-shadow: 0 4px 55px -3px rgba(173, 159, 255, 0.2);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
    content: "";
    border-radius: var(--frame-roundedness);
    box-shadow: inset 0 0 0 2px rgba(236, 244, 255, 1);
  }
`
const Header = styled.div`
  display: flex;
  gap: 0 6px;
  align-items: center;
  width: 100%;
  height: var(--device-ui-height);
  padding: 6px 20px 0;
  background: linear-gradient(85deg, rgba(6, 21, 42, 1), rgba(2, 11, 24, 1));

  &::before {
    display: block;
    width: 19px;
    height: 19px;
    content: "";
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSIxMiIgdmlld0JveD0iMCAwIDggMTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xLjE0ODExIDUuNTM4MkMwLjkyODQ5NCA1Ljc1OTgyIDAuOTI4NDk0IDYuMTI5MTggMS4xNDgxMSA2LjM1MDhMNS44ODIwOSAxMS4xNTI1QzYuMTI2MTEgMTEuMzc0MSA2LjQ5MjE0IDExLjM3NDEgNi43MTE3NiAxMS4xNTI1TDcuMjczMDEgMTAuNTg2MUM3LjQ5MjYyIDEwLjM2NDUgNy40OTI2MiA5Ljk5NTE1IDcuMjczMDEgOS43NDg5MUwzLjUxNTEgNS45MzIxOUw3LjI3MzAxIDIuMTQwMDlDNy40OTI2MiAxLjg5Mzg1IDcuNDkyNjIgMS41MjQ0OSA3LjI3MzAxIDEuMzAyODhMNi43MTE3NiAwLjczNjUyNUM2LjQ5MjE0IDAuNTE0OTA4IDYuMTI2MTEgMC41MTQ5MDggNS44ODIwOSAwLjczNjUyNUwxLjE0ODExIDUuNTM4MloiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=");
    background-repeat: no-repeat;
    background-position: center center;
  }
`
const HeaderImage = styled.img`
  background: white;
  border-radius: 50px;
`
const VendorName = styled.span`
  margin-left: 6px;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: white;
`
const Content = styled.div<any>`
  display: flex;
  flex-flow: column;
  gap: 6px 0;
  height: calc(100% - calc(var(--device-ui-height) * 2) + 200px);
  padding: 0 16px;
  overflow: hidden;
  background: #060816;

  > div {
    opacity: 0;
    transition: opacity 300ms ease;

    ${({ loaded }) =>
      loaded &&
      css`
        opacity: 1;
      `}
  }
`
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--device-ui-height);
  padding: 13px;
  background: linear-gradient(85deg, rgba(6, 21, 42, 1), rgba(2, 11, 24, 1));
`
const FooterInput = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding-left: 24px;
  font-size: 14px;
  line-height: 19px;
  color: white;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 50px;

  &::after {
    display: block;
    width: 28px;
    height: 28px;
    margin-right: 12px;
    content: "";
    background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjkiIGhlaWdodD0iMjkiIHZpZXdCb3g9IjAgMCAyOSAyOSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC4xODc1IiB5PSIwLjE1NDI5NyIgd2lkdGg9IjI4LjI1NTUiIGhlaWdodD0iMjguMzkxMiIgcng9IjE0LjEyNzciIGZpbGw9IndoaXRlIiBmaWxsLW9wYWNpdHk9IjAuMiIvPgo8cGF0aCBkPSJNOS42ODI1MSAxNC45NjUxQzkuOTE5OTEgMTUuMTk2NiAxMC4yNzYgMTUuMTczNCAxMC41MTM0IDE0Ljk2NTFMMTMuMjQzNCAxMi4xNjQ4VjE4LjgwNjlDMTMuMjQzNCAxOS4xMzA5IDEzLjQ4MDggMTkuMzYyMyAxMy44MTMyIDE5LjM2MjNIMTQuNTcyOUMxNC44ODE1IDE5LjM2MjMgMTUuMTQyNiAxOS4xMzA5IDE1LjE0MjYgMTguODA2OVYxMi4xNjQ4TDE3Ljg0ODkgMTQuOTQyQzE4LjA4NjMgMTUuMTczNCAxOC40NDI0IDE1LjE3MzQgMTguNjc5OCAxNC45NjUxTDE5LjIwMjEgMTQuNDU2QzE5LjQxNTcgMTQuMjI0NiAxOS40MTU3IDEzLjg3NzQgMTkuMjAyMSAxMy42NjkxTDE0LjU3MjkgOS4xNTYyMkMxNC4zNTkyIDguOTQ3OTMgMTQuMDAzMSA4Ljk0NzkzIDEzLjc4OTUgOS4xNTYyMkw5LjE2MDI0IDEzLjY2OTFDOC45NDY1OSAxMy44Nzc0IDguOTQ2NTkgMTQuMjI0NiA5LjE2MDI0IDE0LjQ1Nkw5LjY4MjUxIDE0Ljk2NTFaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K");
  }
`

export const Sms: FC<IChatScene> = (props) => {
  const { identity, isAnimMultiScene } = props

  // Refs
  const DeviceRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<AnimeTimelineInstance>()
  const contentContainerRef = useRef<HTMLDivElement>(null)

  // State
  const [isLoaded, setIsLoaded] = useState(false) // Start up the timeline and fade the component (avoids a FOUCing mess)
  const [enableChat, setEnableChat] = useState(false) // Enable the chat after the first scene

  // Handle effects
  useEffect(() => {
    setIsLoaded(true)

    // Handle Anime.js timeline
    timelineRef.current = anime.timeline({
      autoplay: false,
      easing: "easeOutCubic",
      loop: false,
    })

    // Handle timeline animations
    timelineRef.current
      .add({
        begin: () => {
          // Emit an event to start the chat at the same time (otherwise it's already going)
          window.dispatchEvent(new CustomEvent("pxmStartChatAnim"))
        },
        complete: () => timelineRef.current?.pause(),
        delay: 50,
        duration: 1600,
        left: isAnimMultiScene ? ["100%", "0%"] : "0%",
        targets: DeviceRef.current,
      })
      .add({
        complete: () => {
          timelineRef.current?.pause()
          // Emit an event to re-render AnimationPlayer (full reset)
          window.dispatchEvent(new CustomEvent("pxmFullAnimComplete"))
        },
        delay: 150,
        duration: 600,
        opacity: [1, 0],
        scale: [1, 0.9],
        targets: DeviceRef.current,
      })

    // Listen to play events
    const handleDeferredPlayback = () => {
      setEnableChat(true)
      timelineRef.current?.play()
    }
    window.addEventListener("pxmPlayFinalScene", handleDeferredPlayback)

    // Listen to chat end events
    const handleChatEnd = () => {
      timelineRef.current?.play()
    }
    window.addEventListener("pxmChatAnimComplete", handleChatEnd)

    // Cleanup
    return () => {
      // Remove event listeners
      window.removeEventListener("pxmPlayFinalScene", handleDeferredPlayback)
      window.removeEventListener("pxmChatAnimComplete", handleChatEnd)

      // Clear timeline
      timelineRef.current?.pause()
      timelineRef.current = undefined
    }
  }, [])

  return (
    <>
      <Device ref={DeviceRef}>
        <Header>
          <HeaderImage
            alt={identity.chatTitle}
            height="42"
            src={getChatImage(identity.chatImage)}
            width="42"
          />
          <VendorName>{identity.chatTitle}</VendorName>
        </Header>
        <Content loaded={isLoaded} ref={contentContainerRef}>
          {enableChat && (
            <Chat
              {...props}
              containerRef={contentContainerRef.current ?? <></>}
              key="sms-chat"
            />
          )}
        </Content>
        <Footer>
          <FooterInput>Write a reply</FooterInput>
        </Footer>
      </Device>
    </>
  )
}
